import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * useSmartFilters — one hook, three sources: URL → localStorage → defaults.
 *
 * Behaviour
 *  - On mount: hydrate missing URL params from localStorage (except protected keys).
 *  - On change: URL is the source of truth; localStorage mirrors it (debounced).
 *  - `defaults` are used when both URL and storage are empty.
 *  - `protectedKeys` (default `["tab"]`) are never written to storage — they belong
 *    to the router, not the module's filter state.
 *
 * Returned API
 *  - `filters` — plain object of current key→value (strings). Missing = undefined.
 *  - `get(key)` — string | undefined
 *  - `getBool(key)` — true|false (any "1|true|yes" wins)
 *  - `getInt(key, fallback?)` — number | fallback
 *  - `set(key, value)` — value=null|"" removes the key
 *  - `setMany(patch)` — bulk update
 *  - `clear()` — remove every module key (keeps `protectedKeys`)
 */
export default function useSmartFilters(scope, defaults = {}, options = {}) {
  const { protectedKeys = ["tab"] } = options;
  const [params, setParams] = useSearchParams();
  const storageKey = `gp_filters_${scope}`;
  const hydratedRef = useRef(false);

  // 1) Hydrate: if URL has no module-managed key set, restore from storage or defaults
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const next = new URLSearchParams(params);
    let changed = false;
    let stored = {};
    try {
      stored = JSON.parse(localStorage.getItem(storageKey) || "{}") || {};
    } catch { stored = {}; }
    const knownKeys = new Set([...Object.keys(defaults), ...Object.keys(stored)]);
    for (const k of knownKeys) {
      if (protectedKeys.includes(k)) continue;
      if (!next.has(k)) {
        const val = stored[k] ?? defaults[k];
        if (val !== undefined && val !== null && String(val).length > 0) {
          next.set(k, String(val));
          changed = true;
        }
      }
    }
    if (changed) setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot hydration
  }, []);

  // 2) Persist URL → localStorage on every change
  useEffect(() => {
    try {
      const obj = {};
      for (const [k, v] of params.entries()) {
        if (protectedKeys.includes(k)) continue;
        obj[k] = v;
      }
      localStorage.setItem(storageKey, JSON.stringify(obj));
    } catch { /* noop */ }
  }, [params, storageKey, protectedKeys]);

  const filters = useMemo(() => {
    const obj = {};
    for (const [k, v] of params.entries()) obj[k] = v;
    return obj;
  }, [params]);

  const get = useCallback((k) => params.get(k) ?? undefined, [params]);
  const getBool = useCallback((k) => ["1", "true", "yes"].includes((params.get(k) || "").toLowerCase()), [params]);
  const getInt = useCallback((k, fallback) => {
    const raw = params.get(k);
    if (raw === null || raw === undefined || raw === "") return fallback;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? fallback : n;
  }, [params]);

  const set = useCallback((k, value) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === null || value === undefined || value === "" || value === false) next.delete(k);
      else next.set(k, String(value));
      return next;
    }, { replace: false });
  }, [setParams]);

  const setMany = useCallback((patch) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === null || v === undefined || v === "" || v === false) next.delete(k);
        else next.set(k, String(v));
      });
      return next;
    }, { replace: false });
  }, [setParams]);

  const clear = useCallback(() => {
    setParams((prev) => {
      const next = new URLSearchParams();
      for (const k of protectedKeys) if (prev.has(k)) next.set(k, prev.get(k));
      return next;
    });
    try { localStorage.removeItem(storageKey); } catch { /* noop */ }
  }, [setParams, protectedKeys, storageKey]);

  return { filters, get, getBool, getInt, set, setMany, clear };
}
